import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import useLocale from "../hooks/useLocale";
import { fetchFondoById } from "../utils/fetchers";
import { LayoutGrid } from "../components/LayoutContainers";
import { BenchmarkChart } from "../components/BenchmarkChart";
import HeroProductMaxCapital from "../components/ProductDetailPage/HeroProductMaxCapital";
import CollapsableContent from "../components/CollapsableContent";
import { SkeletonProductDetail } from "../components/skeletons/productDetail";
import ElementContainer from "../components/ElementContainer";
import VariationTable from "../components/ProductMaxCapitalDetailPage/VariationTable";
import CompositionCharts from "../components/ProductMaxCapitalDetailPage/CompositionCharts";
import { formatPriceToAR } from "../utils/utils";
import { __ } from "../locales/translation";

export default function ProductMaxCapitalDetailPage() {
    const locale = useLocale();
    const params = useParams();
    const navigate = useNavigate();

    const {
        data: fondoByIdData,
        isLoading: fondoByIdIsLoading,
        isError,
    } = useQuery({
        queryKey: ["fondos", params.productId],
        queryFn: () => fetchFondoById(params.productId),
        select: (data) => {
            return {
                ...data,
                detalle: {
                    ...data.detalle,
                    precioUltimo: formatPriceToAR(data.detalle?.precioUltimo),
                    patrimonioTotal: formatPriceToAR(data.detalle?.patrimonioTotal),
                },
            };
        },
    });

    if (isError) {
        navigate("/error");
    }

    if (!fondoByIdData || fondoByIdIsLoading) {
        return <SkeletonProductDetail />;
    }

    const {
        clase,
        siblings,
        id,
        descripcion,
        tickerBloomberg,
        sociedadGerente,
        variacion,
        esDestacado,
        precioUltimo,
        moneda,
        fechaInicio,
        plazoRescate,
        tipo,
        patrimonioTotal,
        inversionMinima,
        horarioLimite,
        logo,
        objetivo,
        calificacion,
        benchmark,
        composicion,
        fecha,
        honorarios,
    } = fondoByIdData.detalle || {};

    const {
        fondoSeleccionado: { valuaciones },
        fondoBenchmark,
    } = fondoByIdData.variacion_historica || {};

    const documentos = fondoByIdData.documentos || {};

    const title = `Gráfico de ${descripcion} - ${clase}`;

    const heroProps = {
        clase,
        siblings,
        id,
        descripcion,
        tickerBloomberg,
        sociedadGerente,
        variacion,
        esDestacado,
        precioUltimo,
        moneda,
        logo,
        objetivo,
        composicion,
        fecha,
    };

    const collapsableContentProps = {
        fechaInicio,
        plazoRescate,
        tipo,
        moneda,
        patrimonioTotal,
        inversionMinima,
        horarioLimite,
        sociedadGerente,
        calificacion,
        benchmark,
        tickerBloomberg,
        honorarios,
    };

    const variationTableProps = {
        descripcion,
        clase,
        patrimonioTotal,
        variacion,
        precioUltimo,
        fecha,
        benchmark: fondoBenchmark?.variacionFondo,
    };

    return (
        <>
            <HeroProductMaxCapital data={heroProps} />
            <CollapsableContent data={collapsableContentProps} />
            <LayoutGrid spacingTop="0" spacingBottom="2" cols={1} classNames="component-container-type">
                <ElementContainer title={title}>
                    <BenchmarkChart
                        fondoName={descripcion}
                        fondoData={valuaciones}
                        benchmarkData={fondoBenchmark}
                        benchmarkName={benchmark}
                    />
                </ElementContainer>

                <ElementContainer>
                    <VariationTable data={variationTableProps} />
                </ElementContainer>

                <ElementContainer
                    title={__("Composición")}
                    subtitle={`${__("Valores porcentuales sobre el total de la cartera al")}: ${composicion.fecha}`}
                >
                    <CompositionCharts
                        barChartApiData={composicion.instrumentos}
                        pieChartApiData={composicion.agrupaciones}
                    />
                </ElementContainer>

                {documentos && documentos.length > 0 && (
                    <ElementContainer
                        title={__("Descargá la documentación completa")}
                        classNames="cot-download-docs --theme-dark mb-4"
                    >
                        <div className="btn-container">
                            {documentos.map((doc) => {
                                return (
                                    <a
                                        key={doc[locale].name}
                                        className="btn btn-tertiary"
                                        href={doc[locale].url}
                                        target="_blank"
                                    >
                                        {doc[locale].name}
                                    </a>
                                );
                            })}
                        </div>

                        {/* <Button title="Imprimir" relevance="secondary" /> */}
                    </ElementContainer>
                )}
            </LayoutGrid>
        </>
    );
}
